import React, { useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { AiOutlinePlus, AiOutlineDelete } from "react-icons/ai";
import Tooltip from '@mui/material/Tooltip';

// Validation schema for each card
const cardSchema = yup.object().shape({
  tier: yup.string().required("Tier is required"),
  card_holder: yup.string().required("Card holder is required"),
});

// Form schema for an array of cards
const schema = yup.object().shape({
  cards: yup.array().of(cardSchema).min(1, "At least one card is required"),
});

function CardTypeForm({ onSubmit, btnName, updateData, hidebtn }) {
  const {
    control,
    handleSubmit,
    setValues,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      cards: [{ tier: "", card_holder: "" }],
    },
  });

  // Reset form when updateData changes
  useEffect(() => {
    if (updateData) {
      reset({ cards: [{ ...updateData, card_holder: updateData?.card_holder, tier: updateData?.tier }] });
    }
  }, [updateData, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "cards",
  });

  return (
    <div className="max-w-lg mx-auto p-6">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {fields.map((field, index) => (
          <div key={field.id} className="p-1 pb-3 border-b rounded-lg space-y-2 relative">
            <div className="font-semibold text-center">Card Type {index + 1}</div>

            <Controller
              name={`cards.${index}.tier`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Enter tier"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            />
            {errors.cards?.[index]?.tier && (
              <p className="text-red-500 text-sm mt-1">{errors.cards[index].tier.message}</p>
            )}

            <Controller
              name={`cards.${index}.card_holder`}
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  type="text"
                  placeholder="Enter card holder"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            />
            {errors.cards?.[index]?.card_holder && (
              <p className="text-red-500 text-sm mt-1">{errors.cards[index].card_holder.message}</p>
            )}

            {fields.length > 1 && (
              <Tooltip title="Remove Card" placement="top">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="absolute top-0 right-2 text-red-500 hover:text-red-700"
                >
                  <AiOutlineDelete size={20} />
                </button>
              </Tooltip>
            )}
          </div>
        ))}

        <div className="flex gap-3">

          {
            !hidebtn &&
            <Tooltip title="Add More Card" placement="top">
              <button
                type="button"
                onClick={() => append({ tier: "", card_holder: "" })}
                className="flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
              >
                <AiOutlinePlus size={20} />
              </button>
            </Tooltip>
          }
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
          >
            {btnName}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CardTypeForm;
